import { Link, useNavigate } from '@tanstack/react-router';
import { Plus } from 'lucide-react';
import type { ReactNode } from 'react';
import type { InterviewKitView } from '@nest-recruit/shared';
import { Button } from '../../../../components/Button';
import { SkeletonLines } from '../../../../components/Skeleton';
import { EmptyState, ForbiddenState, NotFoundState } from '../../../../components/system-states/SystemState';
import { useCanEdit, useCanView } from '../../../../stores/session';
import { useInterviewKit, useInterviewKits, useRubrics } from '../../api/evaluation-api';
import { KitEditor } from './KitEditor';
import styles from './Evaluation.module.css';

/**
 * ADMIN-04 (F19.04), admin redesign batch 2 Option A: the kits as a strip on top, the open kit's questions grouped under its
 * rubric's lines, and beside them the interviewer's screen. With none chosen, the first opens. The whole page needs Interview
 * kits on (the menu and the route are gated, `module-map.ts`); a job's round picks a kit in its own setup.
 */
export function KitsWorkspace({ selectedId }: { readonly selectedId: string | null }): ReactNode {
  const canView = useCanView('hiring_setup');
  const canEdit = useCanEdit('hiring_setup');
  const kits = useInterviewKits(canView);
  const rubrics = useRubrics(canView);
  const openDetail = useInterviewKit(selectedId === 'new' ? null : selectedId, canView);
  const navigate = useNavigate();

  if (!canView) return <ForbiddenState resource="interview kits" contact="Your admin" />;

  const rows = kits.data ?? [];
  const isNew = selectedId === 'new';
  const openId = isNew ? null : (selectedId ?? rows[0]?.id ?? null);
  // An older version is not in the strip (current versions only); its family still marks the kit it belongs to.
  const openFamily = openDetail.data?.familyId ?? rows.find((row) => row.id === openId)?.familyId ?? null;

  const newButton = canEdit ? (
    <Button variant="solid" size="sm" icon={Plus} onClick={() => void navigate({ to: '/admin/interview-kits/$kitId', params: { kitId: 'new' } })}>
      New kit
    </Button>
  ) : null;

  return (
    <div className={styles.page}>
      <header className={styles.head}>
        <h1 className={styles.title}>
          Interview kits {kits.data && <span className={styles.count}>{rows.length}</span>}
        </h1>
        <div className={styles.headActions}>{rows.length > 0 && !isNew && newButton}</div>
      </header>
      <p className={styles.lede}>
        Suggested questions for each rubric line, so different interviewers gather evidence for the same things. A job's round picks a kit
        in its own setup, and its interviewers see the questions during the interview. Saving writes a new version; rounds keep theirs.
      </p>

      {kits.isPending || rubrics.isPending ? (
        <SkeletonLines count={8} />
      ) : rows.length === 0 && !isNew ? (
        <EmptyState isInset title="No interview kits yet" body="A kit gives every interviewer on a round the same questions, under the rubric's lines." action={newButton ?? undefined} />
      ) : (
        <>
          {rows.length > 0 && (
            <nav className={styles.strip} aria-label="Interview kits">
              {rows.map((row) => (
                <Link key={row.id} to="/admin/interview-kits/$kitId" params={{ kitId: row.id }} className={styles.stripItem} aria-current={!isNew && row.familyId === openFamily ? 'page' : undefined}>
                  <span className={styles.stripName}>{row.name}</span>
                  <span className={styles.stripMeta}>
                    v{row.versionNumber} · {row.questions.length} question{row.questions.length === 1 ? '' : 's'}
                  </span>
                </Link>
              ))}
            </nav>
          )}
          {isNew ? <KitEditor key="new" kit={null} current={null} rubrics={rubrics.data ?? []} /> : <OpenKit id={openId} list={rows} rubrics={rubrics.data ?? []} />}
        </>
      )}
    </div>
  );
}

function OpenKit({ id, list, rubrics }: { readonly id: string | null; readonly list: readonly InterviewKitView[]; readonly rubrics: Parameters<typeof KitEditor>[0]['rubrics'] }): ReactNode {
  const listed = list.find((row) => row.id === id);
  const detail = useInterviewKit(id);
  const kit = detail.data ?? listed ?? null;
  if (id === null) return null;
  if (!kit) return detail.isError ? <NotFoundState /> : <SkeletonLines count={8} />;
  const current = list.find((row) => row.familyId === kit.familyId) ?? null;
  return <KitEditor key={kit.id} kit={kit} current={current} rubrics={rubrics} />;
}
